import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  approveExamSection,
  rejectExamSection,
  requestExamSection,
  updateExamSection,
  type UpdateExamSectionPayload,
} from '@/api/examBookings.api';
import { ApiError } from '@/api/errors';
import { useAuth } from '@/auth/AuthContext';
import { EXAM_APPROVE_ROLES, EXAM_REQUEST_ROLES, hasRole } from '@/auth/roles';
import { showErrorToast, showSuccessToast } from '@/lib/toast';
import { Banner } from '@/components/common/Banner';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { StatusChip } from '@/components/common/StatusChip';
import { FormField } from '@/components/form/FormField';
import { cairoDateString } from '@/lib/calendarDate';
import { formatCurrencyEGP, formatCurrencyUsd, formatDate, formatDateOnly } from '@/lib/format';
import {
  requiresExamDate,
  requiresReserveDate,
  requiresUsdRate,
  toneForBookingStatus,
} from '@/lib/examStatus';
import { BOOKING_STATUSES } from '@/types/shared';
import type {
  BookingStatus,
  ExamPriceRow,
  ExamReservationSnapshot,
  ExamSectionRow,
} from '@/types/shared';
import './ExamSectionPanel.css';

interface LocalState {
  bookingStatus: BookingStatus;
  examDate: string;
  registrationDeadline: string;
  testCenter: string;
  bookingReference: string;
  score: string;
  notes: string;
}

interface FieldErrors {
  examDate?: string;
  usdEgpRate?: string;
  reserveDate?: string;
}

function toLocal(row: ExamSectionRow): LocalState {
  return {
    bookingStatus: row.bookingStatus,
    examDate: row.examDate ? row.examDate.slice(0, 10) : '',
    registrationDeadline: row.registrationDeadline ? row.registrationDeadline.slice(0, 10) : '',
    testCenter: row.testCenter ?? '',
    bookingReference: row.bookingReference ?? '',
    score: row.score ?? '',
    notes: row.notes ?? '',
  };
}

function isSameState(a: LocalState, b: LocalState): boolean {
  return (
    a.bookingStatus === b.bookingStatus &&
    a.examDate === b.examDate &&
    a.registrationDeadline === b.registrationDeadline &&
    a.testCenter === b.testCenter &&
    a.bookingReference === b.bookingReference &&
    a.score === b.score &&
    a.notes === b.notes
  );
}

interface ExamSectionPanelProps {
  studentCode: string;
  row: ExamSectionRow;
  canEdit: boolean;
  onSaved: (updated: ExamSectionRow) => void;
  /** T126 (17-finance-ux.md §6.3) — the section's CURRENT native cost/price, used only to compute
   * the client-side, non-authoritative live EGP preview while entering a FRESH reservation (an
   * already-reserved section previews off its own persisted `financeSnapshot` instead, which needs
   * no separate fetch). `null`/omitted when unavailable — e.g. the viewer's role has no
   * `/finance/exam-pricing` read access (Exam Coordinator, §6.6/§10.8) or the fetch hasn't
   * resolved yet — the preview line is simply omitted in that case, never an error. */
  pricing?: ExamPriceRow | null;
}

/** One of the 4 exam-section panels (SAT/ACT/EST I/EST II) — saves independently via
 * `PATCH /exam-bookings/:studentCode/:examSection` so editing/saving one section never touches
 * the other 3 (mirrors `student-affairs/DocumentRow.tsx`'s per-row independent save pattern).
 * `bookingStatus='Booked'` requires an `examDate` — enforced here client-side (blocks Save +
 * inline field error) mirroring the server's 400 (E4), so the round-trip is a backstop only.
 *
 * The panel is a collapsible disclosure: a clickable header (section name, booking-status chip,
 * and a compact exam-date/status hint) toggles the body (the editable fields, or the read-only
 * summary) via `aria-expanded`/`aria-controls` — mirrors `accounts/InstallmentCard.tsx`'s
 * disclosure pattern so a booking file with several sections reads as a tidy list. Collapsed by
 * default; auto-expands if this section develops a validation/save error so the user can see it
 * without hunting for which panel failed.
 *
 * T126 (17-finance-ux.md §6, extends T34 in place) — additive reservation-with-rate sub-block:
 *  - `showReservationEntry`: editable `usdEgpRate`(SAT/ACT only)/`reserveDate` fields, rendered ONLY
 *    while `bookingStatus==='Booked'` and no `Active` finance snapshot exists yet (§6.1/§6.2) —
 *    Save is blocked (inline field errors, no request sent) until both are valid.
 *  - `showSnapshotBlock`: the persisted, authoritative `financeSnapshot` (§6.3) renders whenever it
 *    exists at all (`Active` or `Reversed`) — independent of the CURRENT `bookingStatus`, so a
 *    cancelled/reversed section still shows its historical reservation with a "Reversed" chip
 *    (§6.5) rather than disappearing.
 *  - `showCorrectionControl`: the secondary "Correct reservation" void+re-reserve action (§6.4,
 *    Q7) — deliberately a SEPARATE mini-form/save path from the section's main Save, so an
 *    ordinary edit (e.g. `testCenter`) while already Booked-with-money NEVER resends
 *    `usdEgpRate`/`reserveDate` and accidentally triggers the server's void+re-reserve path
 *    (confirmed against `ExamBookingsService.updateSection`'s `isReserveIntent` gate, §10.7).
 *  - Cancelling a section with an `Active` snapshot interrupts with a `ConfirmDialog` spelling out
 *    the income+cost reversal before the PATCH fires (§6.5, GAP-1 FINAL); cancelling a
 *    never-reserved (or already-reversed/voided) section is the plain, unchanged T34 status
 *    change.
 *  - §6.6/§10.8: the academy's Cost (EGP) / native cost figures are redacted from both the live
 *    preview and the persisted snapshot specifically for the Exam Coordinator role (Admin sees the
 *    full block) — a client-side-only mitigation, flagged for reviewer sign-off. */
export function ExamSectionPanel({
  studentCode,
  row,
  canEdit,
  onSaved,
  pricing = null,
}: ExamSectionPanelProps) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const baseId = `exam-section-${row.examSection.replace(/\s+/g, '-')}`;
  const bodyId = `${baseId}-body`;

  const [expanded, setExpanded] = useState(false);
  const [local, setLocal] = useState<LocalState>(() => toLocal(row));
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [panelError, setPanelError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  const needsRate = requiresUsdRate(row.examSection);
  const hasActiveSnapshot = row.financeSnapshot?.status === 'Active';
  const showReservationEntry = local.bookingStatus === 'Booked' && !hasActiveSnapshot;
  const showCorrectionControl = local.bookingStatus === 'Booked' && hasActiveSnapshot && canEdit;
  const showSnapshotBlock = Boolean(row.financeSnapshot);
  // §6.6/§10.8 — genuine, acknowledged tension (flagged to reviewer, not fully resolved at the
  // requirements/architecture level): redact the academy's cost figures for Exam Coordinator only.
  const hideCost = user?.role === 'Exam Coordinator';

  // Fresh-reservation entry fields — deliberately NOT part of `LocalState`/`isSameState` (§10.7):
  // they're sent conditionally, never merged into an ordinary field-only save.
  const [reservationRate, setReservationRate] = useState('');
  const [reservationDate, setReservationDate] = useState('');

  // "Correct reservation" (void + re-reserve, §6.4/Q7) — a separate mini-form/save path.
  const [correctionOpen, setCorrectionOpen] = useState(false);
  const [correctionRate, setCorrectionRate] = useState('');
  const [correctionDate, setCorrectionDate] = useState('');
  const [correctionErrors, setCorrectionErrors] = useState<FieldErrors>({});
  const [correctionError, setCorrectionError] = useState<string | null>(null);
  const [correctionSaving, setCorrectionSaving] = useState(false);

  // Cancellation-with-reversal confirm (§6.5, GAP-1 FINAL).
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [reverseDateInput, setReverseDateInput] = useState('');

  // Exam approval workflow (2026-07). Contextual by CURRENT stored status + role.
  const canRequest = hasRole(user?.role, EXAM_REQUEST_ROLES);
  const canApprove = hasRole(user?.role, EXAM_APPROVE_ROLES);
  const [workflowBusy, setWorkflowBusy] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  async function runWorkflow(action: () => Promise<ExamSectionRow>, successKey: string) {
    setWorkflowBusy(true);
    try {
      const updated = await action();
      onSaved(updated);
      setRejectOpen(false);
      setRejectReason('');
      showSuccessToast(t(successKey));
    } catch (err) {
      showErrorToast(err instanceof ApiError ? err.message : t('examBookings.workflow.error'));
    } finally {
      setWorkflowBusy(false);
    }
  }

  // Resync local edit state whenever this section's own canonical row changes (initial load, or
  // after this panel's own successful save) — never while another panel's save is in-flight,
  // since only this panel's `row` prop reference changes on its own update.
  useEffect(() => {
    setLocal(toLocal(row));
    setReservationRate('');
    setReservationDate('');
    setCorrectionOpen(false);
    setCorrectionErrors({});
    setCorrectionError(null);
    setCancelConfirmOpen(false);
  }, [row]);

  // Auto-expand a collapsed panel the moment it develops a validation/save error, so the user
  // doesn't have to guess which of the 4 collapsed sections needs attention.
  useEffect(() => {
    if (panelError || fieldErrors.examDate || fieldErrors.usdEgpRate || fieldErrors.reserveDate) {
      setExpanded(true);
    }
  }, [panelError, fieldErrors]);

  const dirty =
    !isSameState(local, toLocal(row)) ||
    (showReservationEntry && (reservationRate.trim() !== '' || reservationDate.trim() !== ''));

  const hint = local.examDate
    ? t('examBookings.detail.examDateHint', { date: formatDate(local.examDate, i18n.language) })
    : t(`examBookings.status.${local.bookingStatus}`);

  function handleFieldChange<K extends keyof LocalState>(key: K, value: LocalState[K]) {
    setPanelError(null);
    setSavedFlash(false);
    setLocal((prev) => ({ ...prev, [key]: value }));
  }

  function validate(): FieldErrors {
    const errors: FieldErrors = {};
    if (requiresExamDate(local.bookingStatus) && !local.examDate) {
      errors.examDate = t('examBookings.detail.errors.bookedNeedsDate');
    }
    if (showReservationEntry) {
      if (needsRate) {
        const rateNum = Number(reservationRate);
        if (!reservationRate.trim() || Number.isNaN(rateNum) || rateNum <= 0) {
          errors.usdEgpRate = t('examBookings.detail.errors.rateRequired');
        }
      }
      if (requiresReserveDate(local.bookingStatus) && !reservationDate.trim()) {
        errors.reserveDate = t('examBookings.detail.errors.reserveDateRequired');
      }
    }
    return errors;
  }

  function buildPayload(reverseDate?: string): UpdateExamSectionPayload {
    const payload: UpdateExamSectionPayload = {
      bookingStatus: local.bookingStatus,
      examDate: local.examDate || null,
      registrationDeadline: local.registrationDeadline || null,
      testCenter: local.testCenter.trim() || null,
      bookingReference: local.bookingReference.trim() || null,
      score: local.score.trim() || null,
      notes: local.notes.trim() || null,
    };
    // Reservation fields are included ONLY on a genuine fresh-reserve save (§10.7) — never on an
    // ordinary already-Booked-with-money edit, which would otherwise risk the server's
    // void+re-reserve path firing on an unrelated field change.
    if (showReservationEntry) {
      payload.usdEgpRate = needsRate ? Number(reservationRate) : null;
      payload.reserveDate = reservationDate;
    }
    if (reverseDate) {
      payload.reverseDate = reverseDate;
    }
    return payload;
  }

  /** Maps a thrown `ApiError` onto field errors when possible; returns a panel-level message
   * otherwise (or `null` if it was fully absorbed into field errors). */
  function applyApiError(err: unknown): string | null {
    const apiError = ApiError.fromUnknown(err);
    const errs: FieldErrors = {};
    for (const detail of apiError.details) {
      if (detail.field === 'examDate') errs.examDate = detail.message;
      if (detail.field === 'usdEgpRate') errs.usdEgpRate = detail.message;
      if (detail.field === 'reserveDate') errs.reserveDate = detail.message;
    }
    if (Object.keys(errs).length > 0) {
      setFieldErrors((prev) => ({ ...prev, ...errs }));
      return null;
    }
    if (apiError.code === 'BOOKING_ALREADY_REVERSED') {
      return t('examBookings.detail.alreadyReversedError');
    }
    return apiError.message || t('examBookings.detail.errors.saveFailed');
  }

  async function performSave(reverseDate?: string) {
    setPanelError(null);
    setSavedFlash(false);
    setSaving(true);
    try {
      const updated = await updateExamSection(
        studentCode,
        row.examSection,
        buildPayload(reverseDate),
      );
      onSaved(updated);
      setFieldErrors({});
      setSavedFlash(true);
    } catch (err) {
      const message = applyApiError(err);
      if (message) setPanelError(message);
    } finally {
      setSaving(false);
    }
  }

  async function handleSave() {
    setPanelError(null);
    setSavedFlash(false);
    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    // §6.5 GAP-1 FINAL — cancelling a section that actually has money committed (an `Active`
    // snapshot) interrupts with a confirm dialog before the PATCH fires; a never-reserved (or
    // already-reversed) section cancels exactly like today's existing, unchanged T34 behavior.
    const isCancelTransition =
      local.bookingStatus === 'Cancelled' && row.bookingStatus !== 'Cancelled';
    if (isCancelTransition && hasActiveSnapshot) {
      setReverseDateInput(cairoDateString());
      setCancelConfirmOpen(true);
      return;
    }

    await performSave();
  }

  function handleCancelConfirm() {
    setCancelConfirmOpen(false);
    void performSave(reverseDateInput || cairoDateString());
  }

  function handleCancelDecline() {
    setCancelConfirmOpen(false);
  }

  function openCorrection() {
    setCorrectionRate(row.financeSnapshot?.usdEgpRate ?? '');
    setCorrectionDate(row.financeSnapshot?.reserveDate ?? '');
    setCorrectionErrors({});
    setCorrectionError(null);
    setCorrectionOpen(true);
  }

  function closeCorrection() {
    setCorrectionOpen(false);
    setCorrectionErrors({});
    setCorrectionError(null);
  }

  function validateCorrection(): FieldErrors {
    const errors: FieldErrors = {};
    if (needsRate) {
      const rateNum = Number(correctionRate);
      if (!correctionRate.trim() || Number.isNaN(rateNum) || rateNum <= 0) {
        errors.usdEgpRate = t('examBookings.detail.errors.rateRequired');
      }
    }
    if (!correctionDate.trim()) {
      errors.reserveDate = t('examBookings.detail.errors.reserveDateRequired');
    }
    return errors;
  }

  async function handleSaveCorrection(e: FormEvent) {
    e.preventDefault();
    const errors = validateCorrection();
    setCorrectionErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setCorrectionSaving(true);
    setCorrectionError(null);
    try {
      const updated = await updateExamSection(studentCode, row.examSection, {
        bookingStatus: 'Booked',
        usdEgpRate: needsRate ? Number(correctionRate) : null,
        reserveDate: correctionDate,
      });
      onSaved(updated);
      setCorrectionOpen(false);
    } catch (err) {
      const apiError = ApiError.fromUnknown(err);
      const errs: FieldErrors = {};
      for (const detail of apiError.details) {
        if (detail.field === 'usdEgpRate') errs.usdEgpRate = detail.message;
        if (detail.field === 'reserveDate') errs.reserveDate = detail.message;
      }
      if (Object.keys(errs).length > 0) {
        setCorrectionErrors(errs);
      } else {
        setCorrectionError(apiError.message || t('examBookings.detail.errors.saveFailed'));
      }
    } finally {
      setCorrectionSaving(false);
    }
  }

  // Live, non-authoritative EGP preview (§6.3) — client-side estimate only; the server
  // independently computes and snapshots the authoritative figure on save. Scoped to USD sections
  // only (EGP sections have no rate to convert, so nothing to preview beyond the native amount
  // already shown once reserved).
  const freshPreview =
    showReservationEntry && needsRate && pricing
      ? (() => {
          const rateNum = Number(reservationRate);
          if (!Number.isFinite(rateNum) || rateNum <= 0) return null;
          return {
            cost: Number(pricing.costAmount) * rateNum,
            price: Number(pricing.priceAmount) * rateNum,
          };
        })()
      : null;

  const correctionPreview =
    correctionOpen && needsRate && row.financeSnapshot
      ? (() => {
          const rateNum = Number(correctionRate);
          if (!Number.isFinite(rateNum) || rateNum <= 0) return null;
          return {
            cost: Number(row.financeSnapshot!.originalCostAmount) * rateNum,
            price: Number(row.financeSnapshot!.originalPriceAmount) * rateNum,
          };
        })()
      : null;

  function previewHint(preview: { cost: number; price: number } | null) {
    if (!preview) return undefined;
    return (
      <bdi>
        {hideCost
          ? t('examBookings.detail.reservation.estimatedPreviewPriceOnly', {
              price: formatCurrencyEGP(preview.price, i18n.language),
            })
          : t('examBookings.detail.reservation.estimatedPreview', {
              cost: formatCurrencyEGP(preview.cost, i18n.language),
              price: formatCurrencyEGP(preview.price, i18n.language),
            })}
      </bdi>
    );
  }

  function renderSnapshotBlock(snapshot: ExamReservationSnapshot) {
    return (
      <div className="fa-exam-section-panel__reservation">
        <p className="fa-exam-section-panel__block-title">
          {t('examBookings.detail.reservation.title')}
        </p>
        <dl className="fa-exam-section-panel__readonly">
          {needsRate && (
            <ReadonlyRow
              label={t('examBookings.detail.reservation.originalCurrency')}
              value={snapshot.originalCurrency}
            />
          )}
          {needsRate && !hideCost && (
            <ReadonlyRow
              label={t('examBookings.detail.reservation.originalAmounts')}
              value={`${formatCurrencyUsd(Number(snapshot.originalCostAmount), i18n.language)} / ${formatCurrencyUsd(Number(snapshot.originalPriceAmount), i18n.language)}`}
              ltr
              bdi
            />
          )}
          {needsRate && hideCost && (
            <ReadonlyRow
              label={t('examBookings.detail.reservation.originalAmounts')}
              value={formatCurrencyUsd(Number(snapshot.originalPriceAmount), i18n.language)}
              ltr
              bdi
            />
          )}
          {needsRate && (
            <ReadonlyRow
              label={t('examBookings.detail.fields.usdEgpRate')}
              value={String(Number(snapshot.usdEgpRate))}
              ltr
              bdi
            />
          )}
          <ReadonlyRow
            label={t('examBookings.detail.fields.reserveDate')}
            value={formatDateOnly(snapshot.reserveDate, i18n.language)}
            bdi
          />
          {!hideCost && (
            <ReadonlyRow
              label={t('examBookings.detail.reservation.costEgp')}
              value={formatCurrencyEGP(Number(snapshot.costAmountEgp), i18n.language)}
              ltr
              bdi
            />
          )}
          <ReadonlyRow
            label={t('examBookings.detail.reservation.priceEgp')}
            value={formatCurrencyEGP(Number(snapshot.priceAmountEgp), i18n.language)}
            ltr
            bdi
          />
          <div className="fa-exam-section-panel__readonly-row">
            <dt>{t('examBookings.detail.reservation.statusLabel')}</dt>
            <dd>
              <StatusChip
                label={
                  snapshot.status === 'Active'
                    ? t('examBookings.detail.reservation.statusActive')
                    : t('examBookings.detail.reservation.statusReversed', {
                        date: formatDateOnly(snapshot.reversedOn, i18n.language),
                      })
                }
                tone={snapshot.status === 'Active' ? 'success' : 'neutral'}
              />
            </dd>
          </div>
        </dl>

        {showCorrectionControl &&
          (!correctionOpen ? (
            <button type="button" className="fa-btn fa-btn--ghost" onClick={openCorrection}>
              {t('examBookings.detail.reservation.correctButton')}
            </button>
          ) : (
            <form
              className="fa-exam-section-panel__correction"
              onSubmit={handleSaveCorrection}
              noValidate
            >
              <Banner tone="warning">{t('examBookings.detail.reservation.correctWarning')}</Banner>
              <div className="fa-field-row">
                {needsRate && (
                  <FormField
                    id={`${baseId}-correctionRate`}
                    label={t('examBookings.detail.fields.usdEgpRate')}
                    required
                    error={correctionErrors.usdEgpRate}
                    hint={previewHint(correctionPreview)}
                  >
                    <input
                      type="number"
                      min="0"
                      step="0.000001"
                      className="fa-ltr-isolate"
                      value={correctionRate}
                      disabled={correctionSaving}
                      onChange={(e) => {
                        setCorrectionRate(e.target.value);
                        setCorrectionErrors((prev) => ({ ...prev, usdEgpRate: undefined }));
                      }}
                    />
                  </FormField>
                )}
                <FormField
                  id={`${baseId}-correctionDate`}
                  label={t('examBookings.detail.fields.reserveDate')}
                  required
                  error={correctionErrors.reserveDate}
                >
                  <input
                    type="date"
                    value={correctionDate}
                    disabled={correctionSaving}
                    onChange={(e) => {
                      setCorrectionDate(e.target.value);
                      setCorrectionErrors((prev) => ({ ...prev, reserveDate: undefined }));
                    }}
                  />
                </FormField>
              </div>
              <div className="fa-form-actions">
                <button
                  type="button"
                  className="fa-btn fa-btn--secondary"
                  disabled={correctionSaving}
                  onClick={closeCorrection}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="fa-btn fa-btn--primary"
                  disabled={correctionSaving}
                >
                  {correctionSaving
                    ? t('examBookings.detail.saving')
                    : t('examBookings.detail.reservation.saveCorrectionButton')}
                </button>
              </div>
              {correctionError && (
                <p className="fa-field__error" role="alert">
                  {correctionError}
                </p>
              )}
            </form>
          ))}
      </div>
    );
  }

  return (
    <div className="fa-exam-section-panel">
      <button
        type="button"
        className="fa-exam-section-panel__header"
        aria-expanded={expanded}
        aria-controls={bodyId}
        onClick={() => setExpanded((v) => !v)}
      >
        <span className="fa-exam-section-panel__chevron" aria-hidden="true" />
        <span className="fa-exam-section-panel__title">
          {t(`examBookings.sections.${row.examSection}`)}
        </span>
        <StatusChip
          label={t(`examBookings.status.${local.bookingStatus}`)}
          tone={toneForBookingStatus(local.bookingStatus)}
        />
        <span className="fa-exam-section-panel__hint">
          <bdi>{hint}</bdi>
        </span>
      </button>

      <div id={bodyId} className="fa-exam-section-panel__body" hidden={!expanded}>
        {canEdit ? (
          <>
            {/* Exam approval workflow — acts on the persisted status (`row`), not the unsaved select. */}
            {row.bookingStatus === 'Not Booked' && canRequest && (
              <div className="fa-exam-section-panel__workflow">
                <button
                  type="button"
                  className="fa-btn fa-btn--primary"
                  disabled={workflowBusy}
                  onClick={() =>
                    runWorkflow(
                      () => requestExamSection(studentCode, row.examSection),
                      'examBookings.workflow.requested',
                    )
                  }
                >
                  {t('examBookings.workflow.request')}
                </button>
              </div>
            )}

            {row.bookingStatus === 'Requested' && (
              <div className="fa-exam-section-panel__workflow">
                <p className="fa-exam-section-panel__workflow-note">
                  {t('examBookings.workflow.awaiting')}
                </p>
                {canApprove &&
                  (rejectOpen ? (
                    <div className="fa-exam-section-panel__reject">
                      <input
                        type="text"
                        maxLength={255}
                        placeholder={t('examBookings.workflow.reasonPlaceholder')}
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                      />
                      <button
                        type="button"
                        className="fa-btn fa-btn--destructive"
                        disabled={workflowBusy}
                        onClick={() =>
                          runWorkflow(
                            () =>
                              rejectExamSection(studentCode, row.examSection, rejectReason.trim() || undefined),
                            'examBookings.workflow.rejected',
                          )
                        }
                      >
                        {t('examBookings.workflow.confirmReject')}
                      </button>
                      <button
                        type="button"
                        className="fa-btn fa-btn--ghost"
                        disabled={workflowBusy}
                        onClick={() => setRejectOpen(false)}
                      >
                        {t('common.cancel')}
                      </button>
                    </div>
                  ) : (
                    <div className="fa-exam-section-panel__workflow-actions">
                      <button
                        type="button"
                        className="fa-btn fa-btn--primary"
                        disabled={workflowBusy}
                        onClick={() =>
                          runWorkflow(
                            () => approveExamSection(studentCode, row.examSection),
                            'examBookings.workflow.approved',
                          )
                        }
                      >
                        {t('examBookings.workflow.approve')}
                      </button>
                      <button
                        type="button"
                        className="fa-btn fa-btn--ghost"
                        disabled={workflowBusy}
                        onClick={() => setRejectOpen(true)}
                      >
                        {t('examBookings.workflow.reject')}
                      </button>
                    </div>
                  ))}
              </div>
            )}

            {row.bookingStatus === 'Approved' && (
              <div className="fa-exam-section-panel__workflow">
                <p className="fa-exam-section-panel__workflow-note">
                  {t('examBookings.workflow.approvedHint')}
                </p>
              </div>
            )}

            <div className="fa-field-row">
              <div className="fa-field">
                <label htmlFor={`${baseId}-status`}>
                  {t('examBookings.detail.fields.bookingStatus')}
                </label>
                <select
                  id={`${baseId}-status`}
                  value={local.bookingStatus}
                  disabled={saving}
                  onChange={(e) =>
                    handleFieldChange('bookingStatus', e.target.value as BookingStatus)
                  }
                >
                  {/* Requested/Approved are set only via the approval workflow buttons, never this
                      manual select (the server rejects them here). The current stored value is kept
                      as an option so the select can still display it. */}
                  {BOOKING_STATUSES.filter(
                    (status) =>
                      (status !== 'Requested' && status !== 'Approved') ||
                      status === local.bookingStatus,
                  ).map((status) => (
                    <option key={status} value={status}>
                      {t(`examBookings.status.${status}`)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="fa-field">
                <label htmlFor={`${baseId}-examDate`}>
                  {t('examBookings.detail.fields.examDate')}
                  {requiresExamDate(local.bookingStatus) && (
                    <span className="fa-field__required" aria-hidden="true">
                      {' '}
                      *
                    </span>
                  )}
                </label>
                <input
                  id={`${baseId}-examDate`}
                  type="date"
                  value={local.examDate}
                  disabled={saving}
                  aria-invalid={Boolean(fieldErrors.examDate)}
                  onChange={(e) => handleFieldChange('examDate', e.target.value)}
                />
                {fieldErrors.examDate && <p className="fa-field__error">{fieldErrors.examDate}</p>}
              </div>
              <div className="fa-field">
                <label htmlFor={`${baseId}-registrationDeadline`}>
                  {t('examBookings.detail.fields.registrationDeadline')}
                </label>
                <input
                  id={`${baseId}-registrationDeadline`}
                  type="date"
                  value={local.registrationDeadline}
                  disabled={saving}
                  onChange={(e) => handleFieldChange('registrationDeadline', e.target.value)}
                />
              </div>
            </div>

            <div className="fa-field-row">
              <div className="fa-field">
                <label htmlFor={`${baseId}-testCenter`}>
                  {t('examBookings.detail.fields.testCenter')}
                </label>
                <input
                  id={`${baseId}-testCenter`}
                  type="text"
                  maxLength={150}
                  value={local.testCenter}
                  disabled={saving}
                  onChange={(e) => handleFieldChange('testCenter', e.target.value)}
                />
              </div>
              <div className="fa-field">
                <label htmlFor={`${baseId}-bookingReference`}>
                  {t('examBookings.detail.fields.bookingReference')}
                </label>
                <input
                  id={`${baseId}-bookingReference`}
                  type="text"
                  className="fa-ltr-isolate"
                  maxLength={100}
                  value={local.bookingReference}
                  disabled={saving}
                  onChange={(e) => handleFieldChange('bookingReference', e.target.value)}
                />
              </div>
              <div className="fa-field">
                <label htmlFor={`${baseId}-score`}>{t('examBookings.detail.fields.score')}</label>
                <input
                  id={`${baseId}-score`}
                  type="text"
                  className="fa-ltr-isolate"
                  maxLength={50}
                  value={local.score}
                  disabled={saving}
                  onChange={(e) => handleFieldChange('score', e.target.value)}
                />
              </div>
            </div>

            <div className="fa-field">
              <label htmlFor={`${baseId}-notes`}>{t('examBookings.detail.fields.notes')}</label>
              <textarea
                id={`${baseId}-notes`}
                value={local.notes}
                disabled={saving}
                onChange={(e) => handleFieldChange('notes', e.target.value)}
              />
            </div>

            {showSnapshotBlock && renderSnapshotBlock(row.financeSnapshot!)}

            {showReservationEntry && (
              <div className="fa-exam-section-panel__reservation">
                <p className="fa-exam-section-panel__block-title">
                  {t('examBookings.detail.reservation.title')}
                </p>
                <div className="fa-field-row">
                  {needsRate && (
                    <FormField
                      id={`${baseId}-usdEgpRate`}
                      label={t('examBookings.detail.fields.usdEgpRate')}
                      required
                      error={fieldErrors.usdEgpRate}
                      hint={previewHint(freshPreview)}
                    >
                      <input
                        type="number"
                        min="0"
                        step="0.000001"
                        className="fa-ltr-isolate"
                        value={reservationRate}
                        disabled={saving}
                        onChange={(e) => {
                          setReservationRate(e.target.value);
                          setFieldErrors((prev) => ({ ...prev, usdEgpRate: undefined }));
                          setPanelError(null);
                        }}
                      />
                    </FormField>
                  )}
                  <FormField
                    id={`${baseId}-reserveDate`}
                    label={t('examBookings.detail.fields.reserveDate')}
                    required
                    error={fieldErrors.reserveDate}
                  >
                    <input
                      type="date"
                      value={reservationDate}
                      disabled={saving}
                      onChange={(e) => {
                        setReservationDate(e.target.value);
                        setFieldErrors((prev) => ({ ...prev, reserveDate: undefined }));
                        setPanelError(null);
                      }}
                    />
                  </FormField>
                </div>
              </div>
            )}

            <div className="fa-form-actions">
              <button
                type="button"
                className="fa-btn fa-btn--secondary fa-exam-section-panel__save"
                disabled={!dirty || saving}
                onClick={handleSave}
              >
                {saving ? t('examBookings.detail.saving') : t('common.save')}
              </button>
              {savedFlash && !dirty && (
                <span className="fa-exam-section-panel__saved" role="status">
                  {t('examBookings.detail.saved')}
                </span>
              )}
              {panelError && (
                <p className="fa-field__error" role="alert">
                  {panelError}
                </p>
              )}
            </div>
          </>
        ) : (
          <>
            <dl className="fa-exam-section-panel__readonly">
              <ReadonlyRow
                label={t('examBookings.detail.fields.examDate')}
                value={row.examDate ? formatDate(row.examDate, i18n.language) : '—'}
                bdi
              />
              <ReadonlyRow
                label={t('examBookings.detail.fields.registrationDeadline')}
                value={
                  row.registrationDeadline
                    ? formatDate(row.registrationDeadline, i18n.language)
                    : '—'
                }
                bdi
              />
              <ReadonlyRow
                label={t('examBookings.detail.fields.testCenter')}
                value={row.testCenter ?? '—'}
              />
              <ReadonlyRow
                label={t('examBookings.detail.fields.bookingReference')}
                value={row.bookingReference ?? '—'}
                ltr
                bdi
              />
              <ReadonlyRow
                label={t('examBookings.detail.fields.score')}
                value={row.score ?? '—'}
                ltr
                bdi
              />
              <ReadonlyRow label={t('examBookings.detail.fields.notes')} value={row.notes ?? '—'} />
            </dl>
            {showSnapshotBlock && renderSnapshotBlock(row.financeSnapshot!)}
          </>
        )}
      </div>

      <ConfirmDialog
        open={cancelConfirmOpen}
        title={t('examBookings.detail.cancelConfirm.title')}
        description={
          <div className="fa-exam-section-panel__cancel-confirm">
            <p>
              <bdi>
                {t(
                  hideCost
                    ? 'examBookings.detail.cancelConfirm.bodyPriceOnly'
                    : 'examBookings.detail.cancelConfirm.body',
                  {
                    section: t(`examBookings.sections.${row.examSection}`),
                    studentCode,
                    price: formatCurrencyEGP(
                      Number(row.financeSnapshot?.priceAmountEgp ?? 0),
                      i18n.language,
                    ),
                    cost: formatCurrencyEGP(
                      Number(row.financeSnapshot?.costAmountEgp ?? 0),
                      i18n.language,
                    ),
                  },
                )}
              </bdi>
            </p>
            <FormField
              id={`${baseId}-reverseDate`}
              label={t('examBookings.detail.cancelConfirm.reverseDateLabel')}
            >
              <input
                type="date"
                className="fa-ltr-isolate"
                value={reverseDateInput}
                onChange={(e) => setReverseDateInput(e.target.value)}
              />
            </FormField>
          </div>
        }
        confirmLabel={t('examBookings.detail.cancelConfirm.confirmButton')}
        cancelLabel={t('examBookings.detail.cancelConfirm.keepButton')}
        destructive
        onConfirm={handleCancelConfirm}
        onCancel={handleCancelDecline}
      />
    </div>
  );
}

function ReadonlyRow({
  label,
  value,
  ltr,
  bdi,
}: {
  label: string;
  value: string;
  ltr?: boolean;
  bdi?: boolean;
}) {
  return (
    <div className="fa-exam-section-panel__readonly-row">
      <dt>{label}</dt>
      <dd className={ltr ? 'fa-ltr-isolate' : undefined}>{bdi ? <bdi>{value}</bdi> : value}</dd>
    </div>
  );
}
